"use client";

import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { client } from "@/lib/hono";
import { languages, SrclangType } from "@yedalabs/schema";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import toast from "@/lib/toast";
import { t } from "@/lib/localization";

export default function DeleteTranslationModal({
    videoId,
    languageToDelete,
    setLanguageToDelete,
    setReloadVideoKey,
}: {
    videoId: string;
    languageToDelete: SrclangType | null;
    setLanguageToDelete: (lang: SrclangType | null) => void;
    setReloadVideoKey: (key: number) => void;
}) {
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    const languageLabel = languages.find(
        (l) => l.code === languageToDelete
    )?.label;

    const deleteTranslation = async () => {
        setLoading(true);

        if (!languageToDelete) {
            toast.error(t("toast.error.no-selected-language"), {
                keyName: "toast.error.no-selected-language",
            });
            return;
        }

        const res = await client.api.ai["delete-translation"].$post({
            json: {
                languageToDelete,
                videoId,
            },
        });

        if (res.ok) {
            queryClient.invalidateQueries({ queryKey: ["get-video", videoId] });
            setReloadVideoKey(Date.now());
            toast.success(t("toast.success.translation-deleted"), {
                keyName: "toast.success.translation-deleted",
            });
            setLanguageToDelete(null);
        } else {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
        }

        setLoading(false);
    };

    return (
        <AlertDialog
            open={!!languageToDelete}
            onOpenChange={(open) => !open && setLanguageToDelete(null)}
        >
            <AlertDialogContent className="max-w-[400px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {t("video-sheet.translations-tab.delete-modal.title")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {t(
                            "video-sheet.translations-tab.delete-modal.description",
                            undefined,
                            { language: languageLabel || "" }
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>
                        {t("common.cancel")}
                    </AlertDialogCancel>
                    <Button
                        disabled={loading}
                        onClick={deleteTranslation}
                        variant="destructive"
                    >
                        {loading ? (
                            <LoaderCircle className="size-4 animate-spin" />
                        ) : (
                            t("common.delete")
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
